import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { 
  CheckCircle, 
  AlertCircle, 
  Upload, 
  Phone, 
  Mail, 
  MapPin, 
  Building2, 
  FileText,
  Clock,
  Shield,
  User
} from 'lucide-react';

const ClaimProfile = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const providerId = searchParams.get('id');
  
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    verificationMethod: 'email',
    email: '',
    phone: '',
    licenseNumber: '',
    licenseState: '',
    practiceAddress: '',
    businessDocument: null,
    additionalNotes: ''
  });

  // Mock provider data - in production, fetch from API
  const provider = {
    id: providerId || '1',
    name: 'Dr. Gerard Nikodem',
    practice: 'Gerard Nikodem Dental',
    address: '1121 West Gannon Drive, Festus, Missouri, 63028',
    phone: '(636) 937-9133',
    email: 'gcniko@aol.com',
    specialty: 'General Dentist',
    claimed: false,
    verifiedByPlatform: false
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({...formData, businessDocument: file});
    }
  };

  const handleSubmitClaim = async () => {
    // In production, submit to API
    console.log('Submitting claim:', formData);
    setStep(4); // Move to success step
  };

  const renderStepIndicator = () => {
    const steps = [
      { number: 1, title: 'Verify Identity' },
      { number: 2, title: 'Professional Info' },
      { number: 3, title: 'Documentation' },
      { number: 4, title: 'Review & Submit' }
    ];

    return (
      <div className="mb-8">
        <div className="flex items-center justify-between">
          {steps.map((s, index) => (
            <React.Fragment key={s.number}>
              <div className="flex flex-col items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                  step >= s.number 
                    ? 'bg-[#D4AF37] text-[#0A1628]' 
                    : 'bg-gray-200 text-gray-500'
                }`}>
                  {step > s.number ? <CheckCircle className="h-6 w-6" /> : s.number}
                </div>
                <span className={`text-sm mt-2 ${step >= s.number ? 'text-[#0A1628] font-medium' : 'text-gray-500'}`}>
                  {s.title}
                </span>
              </div>
              {index < steps.length - 1 && (
                <div className={`flex-1 h-1 mx-4 ${step > s.number ? 'bg-[#D4AF37]' : 'bg-gray-200'}`}></div>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    );
  };

  const renderStep1 = () => (
    <Card>
      <CardHeader>
        <CardTitle>Verify Your Identity</CardTitle>
        <CardDescription>Choose how you'd like to verify your identity</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Provider Profile Preview */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <div className="flex items-start space-x-4">
            <Avatar className="h-16 w-16">
              <AvatarFallback className="bg-[#0A1628] text-[#D4AF37] text-xl">
                {provider.name.split(' ').map(n => n[0]).join('')}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <h3 className="text-xl font-semibold text-[#0A1628] mb-1">{provider.name}</h3>
              <p className="text-gray-600 mb-2">{provider.practice}</p>
              <div className="space-y-1 text-sm text-gray-600">
                <div className="flex items-center">
                  <MapPin className="h-4 w-4 mr-2" />
                  {provider.address}
                </div>
                <div className="flex items-center">
                  <Badge variant="outline">{provider.specialty}</Badge>
                </div>
              </div>
            </div>
            <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">
              Unclaimed
            </Badge>
          </div>
        </div>

        <div className="space-y-4">
          <p className="font-medium text-[#0A1628]">Select Verification Method:</p>
          
          <div 
            onClick={() => setFormData({...formData, verificationMethod: 'email'})}
            className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
              formData.verificationMethod === 'email' 
                ? 'border-[#D4AF37] bg-[#D4AF37]/5' 
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <div className="flex items-start space-x-3">
              <div className={`p-2 rounded ${
                formData.verificationMethod === 'email' ? 'bg-[#D4AF37]' : 'bg-gray-200'
              }`}>
                <Mail className={`h-5 w-5 ${
                  formData.verificationMethod === 'email' ? 'text-[#0A1628]' : 'text-gray-600'
                }`} />
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-[#0A1628] mb-1">Email Verification</h4>
                <p className="text-sm text-gray-600">We'll send a verification code to your professional email</p>
                {formData.verificationMethod === 'email' && (
                  <div className="mt-3">
                    <Input
                      type="email"
                      placeholder="your.email@practice.com"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                )}
              </div>
              {formData.verificationMethod === 'email' && (
                <CheckCircle className="h-5 w-5 text-[#D4AF37]" />
              )}
            </div>
          </div>

          <div 
            onClick={() => setFormData({...formData, verificationMethod: 'phone'})}
            className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
              formData.verificationMethod === 'phone' 
                ? 'border-[#D4AF37] bg-[#D4AF37]/5' 
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <div className="flex items-start space-x-3">
              <div className={`p-2 rounded ${
                formData.verificationMethod === 'phone' ? 'bg-[#D4AF37]' : 'bg-gray-200'
              }`}>
                <Phone className={`h-5 w-5 ${
                  formData.verificationMethod === 'phone' ? 'text-[#0A1628]' : 'text-gray-600'
                }`} />
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-[#0A1628] mb-1">Phone Verification</h4>
                <p className="text-sm text-gray-600">We'll send a verification code via SMS</p>
                {formData.verificationMethod === 'phone' && (
                  <div className="mt-3">
                    <Input
                      type="tel"
                      placeholder="(555) 123-4567"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                )}
              </div>
              {formData.verificationMethod === 'phone' && (
                <CheckCircle className="h-5 w-5 text-[#D4AF37]" />
              )}
            </div>
          </div>
        </div>

        <div className="flex justify-end space-x-2 pt-4">
          <Button variant="outline" onClick={() => navigate('/doctors')}>
            Cancel
          </Button>
          <Button 
            onClick={() => setStep(2)}
            className="bg-[#D4AF37] text-[#0A1628] hover:bg-[#C19B2B]"
            disabled={
              (formData.verificationMethod === 'email' && !formData.email) ||
              (formData.verificationMethod === 'phone' && !formData.phone)
            }
          >
            Continue
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  const renderStep2 = () => (
    <Card>
      <CardHeader>
        <CardTitle>Professional Information</CardTitle>
        <CardDescription>Provide your professional credentials</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2">
              Professional License Number *
            </label>
            <Input
              placeholder="e.g., D12345"
              value={formData.licenseNumber}
              onChange={(e) => setFormData({...formData, licenseNumber: e.target.value})}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">
              License State *
            </label>
            <select
              value={formData.licenseState}
              onChange={(e) => setFormData({...formData, licenseState: e.target.value})}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"
            >
              <option value="">Select State</option>
              <option value="MO">Missouri</option>
              <option value="IL">Illinois</option>
              <option value="KS">Kansas</option>
              <option value="IA">Iowa</option>
              <option value="AR">Arkansas</option>
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">
            Practice Address *
          </label>
          <Textarea
            placeholder="Enter your practice address"
            value={formData.practiceAddress}
            onChange={(e) => setFormData({...formData, practiceAddress: e.target.value})}
            rows={3}
          />
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
            <div className="text-sm text-blue-900">
              <p className="font-medium mb-1">Verification Process</p>
              <p>We'll verify your credentials with the state licensing board. This typically takes 1-2 business days.</p>
            </div>
          </div>
        </div>

        <div className="flex justify-between pt-4">
          <Button variant="outline" onClick={() => setStep(1)}>
            Back
          </Button>
          <Button 
            onClick={() => setStep(3)}
            className="bg-[#D4AF37] text-[#0A1628] hover:bg-[#C19B2B]"
            disabled={!formData.licenseNumber || !formData.licenseState || !formData.practiceAddress}
          >
            Continue
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  const renderStep3 = () => (
    <Card>
      <CardHeader>
        <CardTitle>Supporting Documentation</CardTitle>
        <CardDescription>Upload documents to verify your practice</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-8">
          <div className="text-center">
            <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h4 className="font-semibold text-[#0A1628] mb-2">Upload Business Document</h4>
            <p className="text-sm text-gray-600 mb-4">
              Accepted: Professional License, Business Card, Practice Letterhead
            </p>
            <input
              type="file"
              id="fileUpload"
              onChange={handleFileUpload}
              accept=".pdf,.jpg,.jpeg,.png"
              className="hidden"
            />
            <label htmlFor="fileUpload">
              <Button
                as="span"
                variant="outline"
                className="cursor-pointer"
              >
                Choose File
              </Button>
            </label>
            {formData.businessDocument && (
              <div className="mt-4 flex items-center justify-center space-x-2 text-sm text-green-600">
                <CheckCircle className="h-4 w-4" />
                <span>{formData.businessDocument.name}</span>
              </div>
            )}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">
            Additional Notes (Optional)
          </label>
          <Textarea
            placeholder="Any additional information to help us verify your profile..."
            value={formData.additionalNotes}
            onChange={(e) => setFormData({...formData, additionalNotes: e.target.value})}
            rows={4}
          />
        </div>

        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <Clock className="h-5 w-5 text-yellow-600 mt-0.5" />
            <div className="text-sm text-yellow-900">
              <p className="font-medium mb-1">Review Timeline</p>
              <p>Most claims are reviewed within 24-48 hours. You'll receive an email notification once your profile is verified.</p>
            </div>
          </div>
        </div>

        <div className="flex justify-between pt-4">
          <Button variant="outline" onClick={() => setStep(2)}>
            Back
          </Button>
          <Button 
            onClick={handleSubmitClaim}
            className="bg-[#D4AF37] text-[#0A1628] hover:bg-[#C19B2B]"
          >
            Submit Claim
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  const renderStep4 = () => (
    <Card>
      <CardContent className="py-12">
        <div className="text-center max-w-md mx-auto">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="h-12 w-12 text-green-600" />
          </div>
          <h2 className="text-3xl font-bold text-[#0A1628] mb-4">Claim Submitted!</h2>
          <p className="text-gray-600 mb-8">
            Thank you for claiming your profile. Our verification team will review your submission and you'll receive an email notification within 24-48 hours.
          </p>
          
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8 text-left">
            <h3 className="font-semibold text-[#0A1628] mb-4">What happens next?</h3>
            <ul className="space-y-3 text-sm text-gray-700">
              <li className="flex items-start">
                <Shield className="h-5 w-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                <span>Our team will verify your credentials with the state licensing board</span>
              </li>
              <li className="flex items-start">
                <FileText className="h-5 w-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                <span>We'll review your supporting documentation</span>
              </li>
              <li className="flex items-start">
                <Mail className="h-5 w-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                <span>You'll receive an email once your profile is approved</span>
              </li>
              <li className="flex items-start">
                <User className="h-5 w-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                <span>After approval, you can manage your complete provider profile</span>
              </li>
            </ul>
          </div>

          <div className="flex justify-center space-x-4">
            <Button
              onClick={() => navigate('/dashboard')}
              className="bg-[#D4AF37] text-[#0A1628] hover:bg-[#C19B2B]"
            >
              Go to Dashboard
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/doctors')}
            >
              View Directory
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-[#0A1628] mb-2">Claim Your Profile</h1>
        <p className="text-gray-600">
          Complete the verification process to manage your provider profile
        </p>
      </div>

      {step < 4 && renderStepIndicator()}

      {step === 1 && renderStep1()}
      {step === 2 && renderStep2()}
      {step === 3 && renderStep3()}
      {step === 4 && renderStep4()}
    </div>
  );
};

export default ClaimProfile;
